import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { AuthContext } from '../context/AuthContext';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './Access.css';
import './ListingChanges.css';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const LISTING_PATH = {
  hotels: '/sy-admin/hotels/edit',
  spas: '/sy-admin/spas/edit',
  tours: '/sy-admin/tours/edit',
  'travel-packages': '/sy-admin/travel-packages/edit',
};

const TYPE_LABEL = { Hotel: 'Hotel', Spa: 'Spa', Tour: 'Tour', TravelPackage: 'Travel Package' };

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const imageUrl = (path) => (/^https?:\/\//.test(path) ? path : `${API_BASE_URL_WITHOUT_API}${path}`);

/* Readable rendering of a before/after value: text, number, list or object */
const renderValue = (value, depth = 0) => {
  if (value === null || value === undefined || value === '') {
    return <span className="lc-empty">—</span>;
  }
  if (typeof value === 'string' && /^\/uploads\//.test(value)) {
    return <img className="lc-thumb" src={imageUrl(value)} alt="" />;
  }
  if (typeof value !== 'object') {
    return <span className="lc-text">{String(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.every((item) => typeof item === 'string' && /^\/uploads\//.test(item))) {
      return (
        <div className="lc-thumbs">
          {value.map((item) => <img key={item} className="lc-thumb" src={imageUrl(item)} alt="" />)}
        </div>
      );
    }
    return (
      <ul className="lc-list">
        {value.map((item, index) => <li key={index}>{renderValue(item, depth + 1)}</li>)}
      </ul>
    );
  }
  return (
    <dl className="lc-object">
      {Object.entries(value).map(([key, inner]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{renderValue(inner, depth + 1)}</dd>
        </div>
      ))}
    </dl>
  );
};

const ListingChanges = () => {
  const { hasPermission } = useContext(AuthContext);
  const canUpdate = hasPermission('listing-changes.update');

  // ?status= (from the dashboard) picks the initial tab; pending by default
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (['pending', 'approved', 'rejected'].includes(searchParams.get('status')) ? searchParams.get('status') : 'pending'));
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');

  // Review dialog
  const [open, setOpen] = useState(null); // full request with changes
  const [openLoading, setOpenLoading] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const load = useCallback(async (status) => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/admin/listing-changes`, { params: { status } });
      setRequests(response.data?.data || []);
      setCounts(response.data?.counts || { pending: 0, approved: 0, rejected: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch change requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(tab);
  }, [tab, load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return requests;
    return requests.filter((item) =>
      (item.listingName || '').toLowerCase().includes(q) ||
      (item.listingCity || '').toLowerCase().includes(q) ||
      (item.ownerPhone || '').includes(q)
    );
  }, [requests, search]);

  const pagination = usePagination(filtered, { noun: 'requests' });

  const openRequest = async (item) => {
    setOpen({ ...item, changes: null });
    setOpenLoading(true);
    setNote('');
    setDialogError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/listing-changes/${item._id}`);
      setOpen(response.data?.data || null);
    } catch (err) {
      setDialogError(err?.response?.data?.message || 'Failed to load the request');
    } finally {
      setOpenLoading(false);
    }
  };

  const decide = async (action) => {
    if (!open) return;
    const verb = action === 'approve' ? 'Approve and publish' : 'Reject';
    if (!window.confirm(`${verb} these changes to "${open.listingName}"?`)) return;
    setBusy(true);
    setDialogError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/listing-changes/${open._id}/${action}`, { note });
      setNotice(response.data?.message || 'Done');
      setOpen(null);
      await load(tab);
    } catch (err) {
      setDialogError(err?.response?.data?.message || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  };

  const renderDialog = () => {
    if (!open) return null;
    const changes = open.changes || [];
    return (
      <div className="customers-modal" onClick={() => !busy && setOpen(null)}>
        <div className="customers-modal-content lc-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="customers-modal-header">
            <div>
              <h2>{open.listingName || 'Listing'} <span className="access-tag access-tag-role">{TYPE_LABEL[open.listingType] || open.listingType}</span></h2>
              <p className="lc-dialog-sub">
                {open.listingCity} · from {open.ownerPhone} · submitted {formatDateTime(open.createdAt)}
              </p>
            </div>
            <button type="button" className="modal-close" onClick={() => setOpen(null)} aria-label="Close">×</button>
          </div>
          <div className="customers-modal-body">
            {dialogError && <div className="error-message">{dialogError}</div>}

            {openLoading ? (
              <p className="access-muted">Loading changes…</p>
            ) : !open.listingExists ? (
              <div className="error-message">The listing no longer exists, so these changes cannot be applied.</div>
            ) : changes.length === 0 ? (
              <p className="access-muted">
                {open.status === 'pending'
                  ? 'Nothing differs from the live listing any more (it may have been edited in the admin since).'
                  : 'This request has been reviewed.'}
              </p>
            ) : (
              <>
                <p className="lc-hint">
                  {changes.length} field{changes.length === 1 ? '' : 's'} would change. The live listing keeps its current values until you approve.
                </p>
                <div className="lc-diff">
                  <div className="lc-diff-head">
                    <span>Field</span><span>Live now</span><span>Owner's change</span>
                  </div>
                  {changes.map((change) => (
                    <div key={change.field} className="lc-diff-row">
                      <div className="lc-diff-field">{change.label}</div>
                      <div className="lc-diff-before">{renderValue(change.before)}</div>
                      <div className="lc-diff-after">{renderValue(change.after)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {open.status !== 'pending' && (
              <p className="lc-reviewed">
                {open.status === 'approved' ? 'Approved' : 'Rejected'} by {open.reviewedByName || '—'} on {formatDateTime(open.reviewedAt)}
                {open.reviewNote && <> · “{open.reviewNote}”</>}
              </p>
            )}

            {open.status === 'pending' && canUpdate && open.listingExists && (
              <div className="lc-decision">
                <label className="cities-field">
                  <span>Note (optional, kept with the request)</span>
                  <input
                    type="text"
                    className="access-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Photos not clear, please re-upload"
                  />
                </label>
                <div className="lc-decision-actions">
                  <Link to={`${LISTING_PATH[open.listingSlug]}/${open.listingId}`} className="btn btn-secondary">
                    Open live listing
                  </Link>
                  <button type="button" className="btn btn-danger" onClick={() => decide('reject')} disabled={busy}>
                    Reject
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => decide('approve')} disabled={busy || changes.length === 0}>
                    {busy ? 'Working…' : 'Approve & publish'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div className="container">
        <div className="access-header">
          <div>
            <h1>Change Requests</h1>
            <p>Edits owners made to live listings. Nothing changes for customers until you approve.</p>
          </div>
          <div className="cities-stats">
            <span className={`access-badge ${counts.pending > 0 ? 'cities-badge-warn' : ''}`}>Pending: {counts.pending}</span>
            <span className="access-badge">Approved: {counts.approved}</span>
            <span className="access-badge">Rejected: {counts.rejected}</span>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {notice && <div className="success-message">{notice}</div>}

        <div className="access-toolbar">
          <div className="lc-tabs">
            {TABS.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`tab-btn ${tab === item.key ? 'active' : ''}`}
                onClick={() => { setTab(item.key); setNotice(''); }}
              >
                {item.label} ({counts[item.key] || 0})
              </button>
            ))}
          </div>
          <input
            className="access-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search listing, city or phone…"
          />
        </div>

        {loading ? (
          <div className="access-table-container">
            <table className="access-table">
              <tbody>
                {[1, 2, 3].map((i) => (
                  <tr key={i}><td><div className="skeleton skeleton-text" style={{ width: '60%', height: 16 }} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <span className="empty-state-icon">✅</span>
            <h2>{tab === 'pending' ? 'Nothing waiting for review' : `No ${tab} requests`}</h2>
            <p>{tab === 'pending' ? 'Edits owners make to live listings will appear here.' : ''}</p>
          </div>
        ) : (
          <div className="access-table-container">
            <table className="access-table">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Listing</th>
                  <th>Type</th>
                  <th>Owner phone</th>
                  <th>Changed</th>
                  {tab !== 'pending' && <th>Reviewed</th>}
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((item) => (
                  <tr key={item._id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(item.createdAt)}</td>
                    <td>
                      <span className="access-name">{item.listingName || '—'}</span>
                      <div className="access-muted">{item.listingCity}</div>
                    </td>
                    <td>{TYPE_LABEL[item.listingType] || item.listingType}</td>
                    <td>{item.ownerPhone}</td>
                    <td>
                      {(item.changedFields || []).length} field{(item.changedFields || []).length === 1 ? '' : 's'}
                      <div className="access-muted lc-fields">{(item.changedFields || []).slice(0, 4).join(', ')}{(item.changedFields || []).length > 4 ? '…' : ''}</div>
                    </td>
                    {tab !== 'pending' && (
                      <td>
                        <span className={`access-tag ${item.status === 'approved' ? 'access-tag-active' : 'access-tag-inactive'}`}>{item.status}</span>
                        <div className="access-muted">{item.reviewedByName} · {formatDateTime(item.reviewedAt)}</div>
                      </td>
                    )}
                    <td>
                      <div className="access-actions">
                        <button type="button" className="btn btn-primary" onClick={() => openRequest(item)}>
                          {tab === 'pending' ? 'Review' : 'View'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination {...pagination.props} />
          </div>
        )}

        {renderDialog()}
      </div>
    </Layout>
  );
};

export default ListingChanges;
